import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import { Button } from '@/components/ui/button'
import { Bold, Italic, Link2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TiptapEditorProps {
  initialContent?: string
  onSave: (html: string) => void
  onCancel?: () => void
  saving?: boolean
}

export function TiptapEditor({ initialContent, onSave, onCancel, saving }: TiptapEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
    ],
    content: initialContent ?? '',
    immediatelyRender: false,
  })

  function handleToggleLink() {
    if (!editor) return
    const previousUrl = editor.getAttributes('link').href
    if (previousUrl) {
      editor.chain().focus().unsetLink().run()
    } else {
      const url = window.prompt('URL du lien')
      if (url) {
        editor.chain().focus().setLink({ href: url }).run()
      }
    }
  }

  function handleSave() {
    if (!editor) return
    const html = editor.getHTML()
    onSave(html)
    editor.commands.clearContent()
  }

  return (
    <div className="border rounded-md">
      <div className="flex items-center gap-1 border-b p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor?.isActive('bold') && 'bg-muted')}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor?.isActive('italic') && 'bg-muted')}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn('h-7 w-7 p-0', editor?.isActive('link') && 'bg-muted')}
          onClick={handleToggleLink}
        >
          <Link2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none min-h-[80px] p-3 focus-within:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[60px]"
      />
      <div className="flex items-center justify-end gap-2 border-t p-2">
        {onCancel && (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            Annuler
          </Button>
        )}
        <Button type="button" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  )
}
